import { useState, useCallback, useRef, useEffect } from 'react';
import { autocompleteAgent } from 'src/utilities/AgentChatAPIs';
import { createDebugLog } from 'src/Ateam-BI/AutoMate/utils/aiDebugLog';

// Gated behind AI_AGENT_DEBUG_LOGS. Logs include raw prefixes and
// backend-returned suggestion titles — keep the flag off in production/beta.
const debugLog = createDebugLog('[useAgentSuggestions]');

// Debounce window, in ms, applied to keystrokes before firing autocompleteAgent.
// See Requirement 10.1.
const DEBOUNCE_MS = 1000;
// Minimum trimmed prefix length. Shorter prefixes clear the list and skip
// the network call. See Requirement 10.2.
const MIN_PREFIX_LENGTH = 3;
// Cap on the number of suggestions returned by the hook. See Requirement 10.4.
const MAX_SUGGESTIONS = 10;
// LRU capacity for the prefix → suggestions cache. See Requirement 10.5.
const CACHE_MAX_ENTRIES = 20;
// `maxResults` passed to autocompleteAgent for suggestions. See Requirement 10.8.
const SEARCH_MAX_RESULTS = 10;

/**
 * Dedupe titles case-insensitively while preserving the first occurrence's
 * original casing, then cap at `MAX_SUGGESTIONS`. The backend already
 * dedupes (Requirement 11.8); this is a defensive second pass in case a
 * future payload drifts.
 */
const dedupeAndCap = (titles) => {
  const seen = new Set();
  const out = [];
  for (const title of titles) {
    if (typeof title !== 'string') continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(title);
    if (out.length >= MAX_SUGGESTIONS) break;
  }
  return out;
};

/**
 * Touch a key in a Map-backed LRU: if the key exists, move it to the end
 * (most-recently-used). The caller is responsible for insertion / eviction.
 */
const touchLru = (cache, key) => {
  if (!cache.has(key)) return;
  const value = cache.get(key);
  cache.delete(key);
  cache.set(key, value);
};

/**
 * Insert a new entry into the LRU, evicting the oldest entry when the cap
 * is exceeded. `Map` preserves insertion order, so the first key is oldest.
 */
const insertLru = (cache, key, value) => {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    cache.delete(oldestKey);
  }
};

/**
 * Hook that produces dynamic search suggestions for the AI search bar by
 * dispatching `autocompleteAgent` calls against the typed prefix and
 * rendering the `suggestions` list returned by the synchronous mutation.
 *
 * The hook owns:
 *   - the 1000ms debounce timer (Requirement 10.1)
 *   - the minimum-prefix guard (Requirement 10.2)
 *   - the `autocompleteAgent` invocation (Requirement 11.1)
 *   - an `AbortController` per fired call; on a new keystroke or unmount,
 *     `controller.abort()` cancels the underlying fetch so the network
 *     round-trip actually terminates (Requirement 11.14 / 10.3)
 *   - an in-memory LRU cache of 20 recent prefixes (Requirement 10.5).
 *     Only non-empty results are cached — an empty list from a real
 *     Bedrock response for an off-topic prefix is NOT cached, so the
 *     agent may return better candidates on retry as its prompt evolves.
 *   - silent error handling — no user-facing banner on failure (Requirement 10.6)
 *   - the `isLoading` state (Requirement 10.7)
 *
 * Consumers wire `onQueryChange` to the AISearchBar's `onChange` and pass
 * `suggestions` / `isLoading` down as props. The primary search flow from
 * the SearchPopup fires an independent `searchAgent` / subscription and
 * does not interact with this hook (Requirement 10.9).
 *
 * @returns {{
 *   suggestions: string[],
 *   isLoading: boolean,
 *   onQueryChange: (prefix: string) => void
 * }}
 */
const useAgentSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Debounce timer for pending autocompleteAgent invocations.
  const debounceTimerRef = useRef(null);
  // AbortController tied to the currently in-flight autocompleteAgent call.
  // Null when nothing is in flight. On supersession (new keystroke) or
  // unmount, we call .abort() so the underlying fetch is cancelled — not
  // just the React state write. See Requirement 11.14.
  const abortControllerRef = useRef(null);
  // Monotonic request counter. Bumped on every new query change (or
  // unmount); any in-flight resolutions for an older requestId fall through
  // the staleness guards and do nothing. Defence-in-depth alongside the
  // AbortController — the controller cancels the network call, the
  // requestId guards against races when JS resolution order leaks a stale
  // resolve before the abort lands.
  const requestIdRef = useRef(0);
  // Tracks whether the component is still mounted. Needed because
  // `autocompleteAgent` is async — its resolution may land after unmount.
  // Re-asserted at the start of the mount effect so StrictMode's
  // setup → cleanup → setup in dev doesn't leave it stuck at false
  // (same bug fix as useAgentChat).
  const mountedRef = useRef(true);
  // LRU cache: trimmed prefix → suggestions array. Component-scoped:
  // created per mount, discarded on unmount. Map preserves insertion order,
  // so the oldest key is always cache.keys().next().value.
  const cacheRef = useRef(new Map());

  const clearDebounceTimer = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  const abortActive = () => {
    if (abortControllerRef.current) {
      // `.abort()` rejects the pending fetch with an AbortError, which is
      // caught silently in `fireSearch`. Safe to call even if the fetch
      // already resolved — no-op in that case.
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = null;
  };

  // Mount / unmount cleanup. See useAgentChat for the mountedRef reassignment
  // rationale — `useRef(true)` runs once and StrictMode's double-invoke
  // would otherwise leave the ref stuck at false after the first cleanup.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Bump the requestId so any still-in-flight resolutions detect
      // they've been superseded by unmount.
      requestIdRef.current += 1;
      clearDebounceTimer();
      abortActive();
    };
  }, []);

  /**
   * Fire the actual autocompleteAgent call for a prefix. Staleness-guarded
   * via `currentRequestId`; cancellation-guarded via the AbortController.
   */
  const fireSearch = (prefix, currentRequestId) => {
    setIsLoading(true);
    debugLog('fireSearch → autocompleteAgent', { prefix, requestId: currentRequestId });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    autocompleteAgent(prefix, SEARCH_MAX_RESULTS, { signal: controller.signal })
      .then((response) => {
        // Staleness guard — a newer onQueryChange may have fired before
        // this promise resolved. The new keystroke already aborted this
        // fetch, but the resolution can still land on the next microtask
        // if it was already in flight to `.then`.
        if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;

        const rawSuggestions = response?.data?.autocompleteAgent?.suggestions;
        if (!Array.isArray(rawSuggestions)) {
          // Null or malformed payload — treat as a failure. Silent clear
          // per Requirement 10.6.
          debugLog('autocompleteAgent returned non-array suggestions — clearing', {
            prefix,
            rawSuggestions,
          });
          setSuggestions([]);
          setIsLoading(false);
          abortControllerRef.current = null;
          return;
        }

        const deduped = dedupeAndCap(rawSuggestions);
        // Only cache non-empty results. Empty arrays from real Bedrock
        // responses (off-topic prefix) are NOT cached so the agent may
        // return better candidates on retry as its prompt evolves.
        if (deduped.length > 0) {
          insertLru(cacheRef.current, prefix, deduped);
        }
        debugLog('autocompleteAgent resolved — suggestions ready', {
          prefix,
          count: deduped.length,
          cached: deduped.length > 0,
        });
        setSuggestions(deduped);
        setIsLoading(false);
        abortControllerRef.current = null;
      })
      .catch((error) => {
        // Staleness guard — a newer onQueryChange may have fired before
        // the rejection landed.
        if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;

        if (error && error.name === 'AbortError') {
          // Superseded by a newer keystroke; the new call owns the
          // loading state. Don't touch suggestions or isLoading here.
          debugLog('autocompleteAgent aborted (superseded)', { prefix });
          return;
        }

        // Transport error, GraphQL errors, or any other real failure —
        // silent clear per Requirement 10.6. No banner, no retry.
        debugLog('autocompleteAgent failed — silently clearing', { prefix, error });
        setSuggestions([]);
        setIsLoading(false);
        abortControllerRef.current = null;
      });
  };

  /**
   * Entry point called from AISearchBar on every keystroke. Responsibilities:
   *   - Trim the prefix.
   *   - Cancel any pending debounce + in-flight fetch (Req 10.3 / 11.14).
   *   - Clear suggestions when the prefix is under the minimum length.
   *   - Serve from the LRU cache on hit without any network call (Req 10.5).
   *   - Otherwise, arm the 1000ms debounce timer.
   */
  const onQueryChange = useCallback((rawPrefix) => {
    const prefix = typeof rawPrefix === 'string' ? rawPrefix.trim() : '';

    // Bump first so any still-in-flight resolutions from the previous
    // prefix see they've been superseded and exit via the staleness guards.
    const currentRequestId = ++requestIdRef.current;

    debugLog('onQueryChange', { prefix, length: prefix.length, requestId: currentRequestId });

    // Tear down any pending timer and in-flight fetch before we decide
    // the next state.
    clearDebounceTimer();
    abortActive();

    if (prefix.length < MIN_PREFIX_LENGTH) {
      // Requirement 10.2: short prefixes clear the list and skip the call.
      debugLog('prefix below minimum — clearing suggestions', { prefix, min: MIN_PREFIX_LENGTH });
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Cache hit — serve immediately, skip the network entirely (Req 10.5).
    if (cacheRef.current.has(prefix)) {
      const cached = cacheRef.current.get(prefix);
      touchLru(cacheRef.current, prefix);
      debugLog('cache hit', { prefix, count: cached.length });
      setSuggestions(cached);
      setIsLoading(false);
      return;
    }

    // Arm the debounce. If another onQueryChange fires before this
    // timer elapses, the new call will clear this timer and start its own
    // — no network call is made for the abandoned prefix (Req 10.3).
    debugLog('arming debounce', { prefix, delayMs: DEBOUNCE_MS });
    debounceTimerRef.current = setTimeout(() => {
      // Final staleness check: unmount or newer call since we armed.
      if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
      fireSearch(prefix, currentRequestId);
    }, DEBOUNCE_MS);
  }, []);

  return { suggestions, isLoading, onQueryChange };
};

export default useAgentSuggestions;
